import { type ExportResult, ExportResultCode } from '@opentelemetry/core';
import type { LogRecordExporter, ReadableLogRecord } from '@opentelemetry/sdk-logs';
import {
  createCloudWatchTransport,
  createLazyLogger,
  createOtelTransport,
  enrichDealerContextPlugin,
  LogLevel,
} from '@org-avp/logger/otel-layer';
import { enrichThingContextPlugin } from '@org-avp/naap-models';
import type { LoggerContext } from '@/lib/logging';
import * as packageJson from '../../package.json';
import { STAGE } from './env';

export const logger = createLazyLogger<LoggerContext>({
  transports: [
    createOtelTransport({
      level: STAGE === 'prod' ? LogLevel.debug : LogLevel.trace,
      consoleDebug: STAGE === 'develop',
    }),
    createCloudWatchTransport({
      enabled: false,
      level: LogLevel.info,
      appMetaData: {
        app: 'naap-kiosk',
        stage: STAGE,
        version: packageJson.version,
      },
      onError: (err) => {
        console.error('❌ CloudWatch transport error:', err.message);

        if (err.cause) {
          console.error('Full AWS Error (untruncated):', JSON.stringify(err.cause, null, 2));
        }
      },
    }),
  ],
  plugins: [enrichDealerContextPlugin(), enrichThingContextPlugin()],
});

/**
 * Wraps a LogRecordExporter to retry failed exports with exponential backoff
 * using a serial queue (only one export in-flight at a time).
 *
 * The default BatchLogRecordProcessor splices records out of its buffer BEFORE
 * calling export, then discards the batch on failure. This wrapper catches
 * failures at the exporter level and re-attempts before reporting the final
 * result to the processor.
 *
 * The serial queue prevents a "retry storm" where multiple concurrent retry
 * chains compete for the browser's 64 KB keepalive budget — the root cause of
 * startup log loss.
 *
 * @deprecated Kept as reference implementation. Not currently wired to logger.
 */
export class RetryingLogExporter implements LogRecordExporter {
  private readonly maxRetries = 3;
  private readonly baseDelayMs = 2000;

  /** Serial queue: each entry is an export job waiting to run. */
  private queue: Array<{
    logs: ReadableLogRecord[];
    resultCallback: (result: ExportResult) => void;
  }> = [];
  private busy = false;

  constructor(private readonly inner: LogRecordExporter) {}

  export(logs: ReadableLogRecord[], resultCallback: (result: ExportResult) => void): void {
    this.queue.push({ logs, resultCallback });
    this.drain();
  }

  shutdown(): Promise<void> {
    return this.inner.shutdown();
  }

  forceFlush(): Promise<void> {
    return this.inner.forceFlush();
  }

  /** Process the queue one job at a time. */
  private drain(): void {
    if (this.busy || this.queue.length === 0) return;
    this.busy = true;

    const job = this.queue.shift();
    if (!job) {
      this.busy = false;
      return;
    }
    this.exportWithRetry(job.logs, 0, (result) => {
      job.resultCallback(result);
      this.busy = false;
      this.drain(); // process next job
    });
  }

  private exportWithRetry(
    logs: ReadableLogRecord[],
    attempt: number,
    resultCallback: (result: ExportResult) => void,
  ): void {
    this.inner.export(logs, (result) => {
      if (result.code === ExportResultCode.SUCCESS || attempt >= this.maxRetries) {
        resultCallback(result);
        return;
      }

      // Exponential backoff: 2s, 4s, 8s
      const delay = this.baseDelayMs * 2 ** attempt;
      // eslint-disable-next-line no-console
      console.warn(
        `[RetryingLogExporter] Export failed (attempt ${attempt + 1}/${this.maxRetries}), ` +
          `retrying in ${delay}ms: ${result.error?.message ?? 'unknown error'}`,
      );

      setTimeout(() => {
        this.exportWithRetry(logs, attempt + 1, resultCallback);
      }, delay);
    });
  }
}

window.addEventListener('error', (e) => {
  logger.withError(e.error).warn('Uncaught error');
});

window.addEventListener('unhandledrejection', (e) => {
  logger.withError(e.reason).warn('Unhandled promise rejection');
});
